import { CopyToNewSelectionModal } from '@app/components/orders/CopyToNewSelectionModal';
import { SelectionImportModal } from '@app/components/orders/SelectionImportModal';
import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { useDropdownContext } from '@app/components/various/Dropdown/context/DropdownContext';
import { DropdownDrawer } from '@app/components/various/Dropdown/DropdownDrawer';
import { DropdownPopover } from '@app/components/various/Dropdown/DropdownPopover';
import { MoreHorizontallySmall } from '@app/components/various/Icons';
import { Portal } from '@app/components/various/Portal';
import { Scrim } from '@app/components/various/Scrim';
import { Tooltip } from '@app/components/various/Tooltip';
import { getIsCsvImportEnabled } from '@app/ducks';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useOrderOptions } from '@app/utils/hooks/order/useOrderOptions';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { isDefined } from '@app/utils/is';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { OrderOptionsDrawer } from './OrderOptionsDrawer';
import { OrderSelectPopover } from './OrderSelectPopover';
import { OrderSelectTrigger } from './OrderSelectTrigger';

export const OrderOptions = () => {
  const { t } = useTranslation(['common']);
  const { showModal, isOpen, hideModal } = useModalToggle();
  const isCsvImportEnabled = useSelector(getIsCsvImportEnabled);
  const dropdownItems = useOrderOptions({ onCopyToNewSelection: showModal });
  const { pathname } = useLocation();
  const { activeParentItemKey, isMobile, isDropdownOpen, closeDropdown, layerProps } = useDropdownContext();
  const { orderId, buyerId, orderNumber } = useAvailableOrderDetails();
  const { trackEvent } = useAmplitude();

  const handleCopySelectionSubmit = () => {
    trackEvent({
      name: 'bottomBar.button.copyselection.click',
      properties: { path: pathname.includes('products') ? 'allProducts' : 'mySelection' },
    });
  };

  return (
    <div className="-m-1 flex shrink-0 items-center gap-2 overflow-hidden p-1 sm:shrink">
      <DropdownTrigger>
        {isMobile ?
          <Button icon={MoreHorizontallySmall} color="secondary" />
        : <OrderSelectTrigger />}
      </DropdownTrigger>
      {isMobile ?
        <Portal>
          <Scrim isVisible={isDropdownOpen} onClick={closeDropdown} />
          <DropdownDrawer
            title={t('common:option_other')}
            isOpen={isDropdownOpen}
            isHidden={isDefined(activeParentItemKey)}
            onClose={closeDropdown}
          >
            <OrderOptionsDrawer items={dropdownItems} />
          </DropdownDrawer>
        </Portal>
      : <>
          {layerProps.renderLayer(
            <DropdownPopover
              isOpen={isDropdownOpen}
              className="-ml-5 flex h-80 w-full max-w-md flex-col p-0"
              onClose={closeDropdown}
              {...layerProps.layerProps}
            >
              <OrderSelectPopover />
            </DropdownPopover>,
          )}
          {dropdownItems.length === 1 ?
            <Tooltip content={dropdownItems[0]?.label}>
              <Button
                color="secondary"
                icon={dropdownItems[0]?.icon}
                disabled={dropdownItems[0]?.isDisabled}
                aria-label={dropdownItems[0]?.label}
                onClick={dropdownItems[0]?.onClick}
              />
            </Tooltip>
          : <Dropdown title={t('common:option_other')} placement="top-start">
              <DropdownTrigger>
                <Button color="secondary" icon={MoreHorizontallySmall} aria-label={t('common:option_other')} />
              </DropdownTrigger>
              <DropdownMenu items={dropdownItems} />
            </Dropdown>
          }
        </>
      }
      <CopyToNewSelectionModal
        isOpen={isOpen}
        onClose={hideModal}
        orderId={orderId}
        orderNumber={orderNumber}
        defaultBuyer={buyerId}
        onSubmit={handleCopySelectionSubmit}
      />
      {isCsvImportEnabled && <SelectionImportModal />}
    </div>
  );
};
